import React, { Suspense } from 'react';
import { WagmiProvider } from 'wagmi';
import ReactDOM from 'react-dom/client';
import { RainbowKitProvider } from '@rainbow-me/rainbowkit';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App.tsx';
import { config } from './wagmiConfig';

import './index.scss';
import '@tempusfinance/common-ui/dist/index.css';
import '@rainbow-me/rainbowkit/styles.css';
import StarknetProvider from './components/StarknetProvider';

// ---------------------------------------------------------------------------
// Global error mitigations
// ---------------------------------------------------------------------------

// Vite dynamic-import chunk failures — happens when users have a stale tab open
// after a new deployment and the old chunk hashes no longer exist on the server.
// Auto-reload once so the browser fetches the latest assets.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  // Guard against infinite reload loops — only reload once per session.
  const reloadedKey = 'nostra:chunk-reload';
  if (!sessionStorage.getItem(reloadedKey)) {
    sessionStorage.setItem(reloadedKey, '1');
    window.location.reload();
  }
});

// Suppress known wallet-extension and provider errors that surface as unhandled
// promise rejections. These originate from browser-extension RPC layers or EVM
// providers and are not actionable from application code.
const SUPPRESSED_REJECTION_PATTERNS = [
  'Object Not Found Matching Id',       // Wallet extension RPC desync
  'provider is disconnected',           // EVM provider losing connection
  'refresh-required',                    // Wallet extension signalling a refresh
  'Proposal expired',                    // WalletConnect proposal TTL exceeded (starknetkit)
];

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const message =
    typeof reason === 'string'
      ? reason
      : reason instanceof Error
        ? reason.message
        : typeof reason === 'object' && reason !== null
          ? JSON.stringify(reason)
          : String(reason);

  if (SUPPRESSED_REJECTION_PATTERNS.some((pattern) => message.includes(pattern))) {
    event.preventDefault(); // Prevent PostHog / console noise
  }
});

// ---------------------------------------------------------------------------

// Lazy-load PostHog analytics — not needed for initial render
const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
if (posthogKey) {
  import('posthog-js').then(({ default: posthog }) => {
    posthog.init(posthogKey, {
      api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
      person_profiles: 'identified_only',
      capture_pageview: true,
      capture_pageleave: true,
    });
  });
}

const queryClient = new QueryClient();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <WagmiProvider config={config}>
      <StarknetProvider>
        <QueryClientProvider client={queryClient}>
          <RainbowKitProvider modalSize="compact">
            {/* Fallback keeps the same visual as the inline HTML loader in index.html so there's no flash. */}
            <Suspense fallback={
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                backgroundColor: 'var(--surface-body, #ebedfa)',
              }}>
                <svg width="48" height="48" viewBox="0 0 77.93 75.16" xmlns="http://www.w3.org/2000/svg" style={{ animation: 'pulse 1.5s ease-in-out infinite' }}>
                  <path d="M0,43.7L31.2,25.69,0,7.67V43.7Zm38.97-13.53L0,52.67l38.97,22.5,38.96-22.5V7.67L38.97,30.17Zm-.49-30.17L20.6,10.6l18.36,10.6,18.36-10.6L38.47,0Z" fill="#ff4240" />
                </svg>
              </div>
            }>
              <App />
            </Suspense>
          </RainbowKitProvider>
        </QueryClientProvider>
      </StarknetProvider>
    </WagmiProvider>
  </React.StrictMode>,
);
